import React, { useEffect, useState } from 'react'
import { Form, Button, Modal, Checkbox } from 'semantic-ui-react'
import {
    FormTextAreaFormField,
    FormTextFormField,
    SubmitCancelButtons,
    AppPrimaryButton,
    FormDateField,
    FormDropdownField,
} from 'elements'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { FormErrorText, MediaService } from 'utility'
import { FileUploadPicker, FlexRowBetween, TitleText } from 'components'
import { useCustomerGroups } from 'hooks/customer'
import { CustomerService } from 'utility/services/customer'
import { AuthService } from 'utility/services/auth'
import { X, PencilSimple } from 'phosphor-react'
import { DesktopMode, MobileMode } from 'layout'

const CustomerSchema = yup.object().shape({
    CustomerGroupId: yup.string().required('Customer Group is required'),
    customer_name: yup
        .string()
        .trim('Customer name does not allow spaces')
        .strict()
        .required('Customer Name is required'),
    Address: yup.string().trim(FormErrorText.SpacesNotAllowed).strict(),
    City: yup.string().trim(FormErrorText.SpacesNotAllowed).strict(),
    State: yup.string().trim(FormErrorText.SpacesNotAllowed).strict(),
    Zip: yup.string().trim(FormErrorText.SpacesNotAllowed).strict(),
    Email: yup
        .string()
        .email('Please enter a valid email address')
        .trim(FormErrorText.SpacesNotAllowed)
        .strict(),
    Phone: yup
        .string()
        .trim(FormErrorText.SpacesNotAllowed)
        .strict()
        .nullable(),
})

const additionalInfoSchema = yup.object().shape({
    label: yup.string().required('Label is required'),
    labelType: yup.string().required('Label Type is required'),
    value: yup.mixed().when('labelType', {
        is: 'numeric',
        then: yup
            .number()
            .typeError('Value must be a number')
            .required('Value is required'),
        otherwise: yup.string().required('Value is required'),
    }),
    showInReport: yup.boolean(),
})

const labelTypeOptions = [
    { key: 'numeric', value: 'numeric', text: 'Numeric' },
    { key: 'text', value: 'text', text: 'Text' },
    { key: 'textarea', value: 'textarea', text: 'Text Area' },
    { key: 'datetime', value: 'datetime', text: 'Date Time' },
    { key: 'date', value: 'date', text: 'Date Only' },
]

// FieldType mapping for additional info
const labelTypeToFieldType = {
    numeric: 1,
    text: 2,
    textarea: 3,
    datetime: 4,
    date: 5,
}

// Helper to map FieldType to labelType string
const fieldTypeToLabelType = {
    1: 'numeric',
    2: 'text',
    3: 'textarea',
    4: 'datetime',
    5: 'date',
}

// Helper to filter out empty additional info entries
const filterNonEmptyAdditionalInfo = list =>
    (list || []).filter(info => info.FieldName || info.FieldValue)

export const CustomerAddEditModal = ({
    onClose,
    data,
    loading,
    additionalInfoList = [],
    setAdditionalInfoList,
    additionalInfoModalOpen,
    setAdditionalInfoModalOpen,
    customerList = [],
}) => {
    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
        watch,
    } = useForm({
        resolver: yupResolver(CustomerSchema),
    })

    // Get user info from AuthService
    const user = AuthService.getUser()
    const companyId = user?.Companies?.[0]?.CompanyId || 1
    const createdBy = user?.unique_id || 1

    // Customer Groups
    const [groupRefreshKey, setGroupRefreshKey] = useState(0)
    const customerGroups = useCustomerGroups(companyId, groupRefreshKey)
    const [groupModalOpen, setGroupModalOpen] = useState(false)
    const [groupModalMode, setGroupModalMode] = useState('add') // 'add' or 'edit'
    const [newGroupName, setNewGroupName] = useState('')
    const [groupEmail, setGroupEmail] = useState('')
    const [groupPhone, setGroupPhone] = useState('')
    const [addressLine1, setAddressLine1] = useState('')
    const [addressLine2, setAddressLine2] = useState('')
    const [city, setCity] = useState('')
    const [stateProvince, setStateProvince] = useState('')
    const [postalCode, setPostalCode] = useState('')
    const [country, setCountry] = useState('')
    const [logoFiles, setLogoFiles] = useState([])

    const getSelectedLogoFile = () => {
        const first = (logoFiles && logoFiles[0]) || null
        if (!first) return null
        // FileUploadPicker returns different shapes depending on maxFiles flag
        // Case 1 (maxFiles===1): { file: File, url, local }
        if (first.file && typeof first.file === 'object' && first.file instanceof File) {
            return first.file
        }
        // Case 2 (multiple): { file: { name, file: File }, ... }
        if (first.file && first.file.file) {
            return first.file.file
        }
        // If the object itself is a File
        if (first instanceof File) {
            return first
        }
        return null
    }
    const [editGroupId, setEditGroupId] = useState(null)
    const [pendingSelectGroupId, setPendingSelectGroupId] = useState(null)
    const [pendingSelectGroupName, setPendingSelectGroupName] = useState(null)
    const [groupNameTriedSubmit, setGroupNameTriedSubmit] = useState(false)
    const [isInactiveSelected, setIsInactiveSelected] = useState(false)

    // If setAdditionalInfoList is provided, use it; otherwise, use local state
    const [localAdditionalInfoList] = useState([])
    const effectiveAdditionalInfoList = setAdditionalInfoList
        ? additionalInfoList
        : localAdditionalInfoList

    // New: state for editing which additional info entry
    const [editAdditionalInfoIndex, setEditAdditionalInfoIndex] = useState(null)

    useEffect(() => {
        if (data?.unique_id) {
            reset({
                CustomerGroupId: data.CustomerGroupId
                    ? data.CustomerGroupId.toString()
                    : '',
                customer_name: data.customer_name,
                Address: data.Address,
                City: data.City,
                State: data.State,
                Zip: data.Zip,
                unique_id: data.unique_id,
                Email: data.Email,
                Phone: data.Phone,
            })
        } else {
            reset({ CustomerGroupId: '' })
        }
    }, [data])

    const submit = async formData => {
        // If editing additional info, find the full customer object from the list
        let payload
        if (customerList.length && data?.unique_id) {
            const fullCustomer = customerList.find(
                c => c.unique_id === data.unique_id,
            )
            payload = {
                ...fullCustomer,
                CustomerAdditionalInfo: filteredAdditionalInfoList,
            }
        } else {
            payload = {
                ...data,
                ...formData,
                CustomerGroupId: Number(formData.CustomerGroupId),
                CustomerAdditionalInfo: filteredAdditionalInfoList,
            }
        }
        try {
            await CustomerService.update(payload)
            onClose(payload)
        } catch (e) {
            onClose(payload)
        }
    }
    const onCancel = () => {
        onClose()
    }
    const openAddGroupModal = () => {
        setGroupModalMode('add')
        setNewGroupName('')
        setGroupEmail('')
        setGroupPhone('')
        setAddressLine1('')
        setAddressLine2('')
        setCity('')
        setStateProvince('')
        setPostalCode('')
        setCountry('')
        setLogoFiles([])
        setEditGroupId(null)
        setGroupModalOpen(true)
        setGroupNameTriedSubmit(false)
    }
    const openEditGroupModal = () => {
        const selectedId = watch('CustomerGroupId')
        if (!selectedId) return
        const group = customerGroups.find(g => g.CustomerGroupId.toString() === selectedId)
        setGroupModalMode('edit')
        setNewGroupName(group ? group.CustomerGroupName : '')
        setGroupEmail(group?.Email || '')
        setGroupPhone(group?.Phone || '')
        setAddressLine1(group?.AddressLine1 || '')
        setAddressLine2(group?.AddressLine2 || '')
        setCity(group?.City || '')
        setStateProvince(group?.StateProvince || '')
        setPostalCode(group?.PostalCode || '')
        setCountry(group?.Country || '')
        setLogoFiles(group?.LogoUrl ? [{ url: group.LogoUrl, id: 'logo' }] : [])
        setEditGroupId(selectedId)
        setGroupModalOpen(true)
        setGroupNameTriedSubmit(false)
    }
    const handleGroupModalSubmit = async () => {
        setGroupNameTriedSubmit(true)
        if (!newGroupName.trim()) return
        if (groupModalMode === 'add') {
            let logoUrl = ''
            try {
                const mediaFile = getSelectedLogoFile()
                if (mediaFile) {
                    const upload = await MediaService.uploadMedia({ media: mediaFile })
                    logoUrl = upload?.ImagePathUrl || upload?.FileURL || ''
                } else if (logoFiles?.[0]?.url) {
                    logoUrl = logoFiles[0].url
                }
            } catch (e) {
                // ignore upload failure; allow creation without logo
            }

            const result = await CustomerService.addCustomerGroup({
                CustomerGroupName: newGroupName,
                CompanyId: companyId,
                CreatedBy: createdBy,
                IsActive: true,
                Email: groupEmail || null,
                Phone: groupPhone || null,
                AddressLine1: addressLine1 || null,
                AddressLine2: addressLine2 || null,
                City: city || null,
                StateProvince: stateProvince || null,
                PostalCode: postalCode || null,
                Country: country || null,
                LogoUrl: logoUrl || null,
            })
            if (result && result.CustomerGroupId) {
                setPendingSelectGroupId(result.CustomerGroupId.toString())
                setPendingSelectGroupName(null)
            } else {
                setPendingSelectGroupId(null)
                setPendingSelectGroupName(newGroupName)
            }
        } else if (groupModalMode === 'edit' && editGroupId) {
            let logoUrl = ''
            try {
                const mediaFile = getSelectedLogoFile()
                if (mediaFile) {
                    const upload = await MediaService.uploadMedia({ media: mediaFile })
                    logoUrl = upload?.ImagePathUrl || upload?.FileURL || ''
                } else if (logoFiles?.[0]?.url) {
                    logoUrl = logoFiles[0].url
                }
            } catch (e) {
                // ignore logo upload failure in edit
            }
            await CustomerService.updateCustomerGroup({
                CustomerGroupName: newGroupName,
                CompanyId: companyId,
                CreatedBy: createdBy,
                CustomerGroupId: editGroupId,
                IsActive: !isInactiveSelected,
                Email: groupEmail || null,
                Phone: groupPhone || null,
                AddressLine1: addressLine1 || null,
                AddressLine2: addressLine2 || null,
                City: city || null,
                StateProvince: stateProvince || null,
                PostalCode: postalCode || null,
                Country: country || null,
                LogoUrl: logoUrl || null,
            })
        }
        setGroupModalOpen(false)
        setNewGroupName('')
        setGroupEmail('')
        setGroupPhone('')
        setAddressLine1('')
        setAddressLine2('')
        setCity('')
        setStateProvince('')
        setPostalCode('')
        setCountry('')
        setLogoFiles([])
        setEditGroupId(null)
        setIsInactiveSelected(false)
        setGroupRefreshKey(prev => prev + 1)
    }

    useEffect(() => {
        // Try by ID first
        if (
            pendingSelectGroupId &&
            customerGroups.some(
                g => g.CustomerGroupId.toString() === pendingSelectGroupId,
            )
        ) {
            setValue('CustomerGroupId', pendingSelectGroupId)
        }
        // Fallback: Try by name if ID is not available
        if (
            pendingSelectGroupName &&
            customerGroups.some(
                g => g.CustomerGroupName === pendingSelectGroupName,
            )
        ) {
            const found = customerGroups.find(
                g => g.CustomerGroupName === pendingSelectGroupName,
            )
            if (found) {
                setValue('CustomerGroupId', found.CustomerGroupId.toString())
                setPendingSelectGroupName(null)
            }
        }
    }, [customerGroups, pendingSelectGroupId, pendingSelectGroupName, setValue])

    useEffect(() => {
        if (
            pendingSelectGroupId &&
            watch('CustomerGroupId') === pendingSelectGroupId
        ) {
            setPendingSelectGroupId(null)
        }
    }, [pendingSelectGroupId, watch, setPendingSelectGroupId])

    // Pre-fill additional info form fields when editing
    useEffect(() => {
        if (
            additionalInfoModalOpen &&
            effectiveAdditionalInfoList &&
            effectiveAdditionalInfoList.length > 0
        ) {
            const info = effectiveAdditionalInfoList[0]
            setValue('label', info.FieldName || '')
            setValue('labelType', fieldTypeToLabelType[info.FieldType] || '')
            // Robust date/datetime-local handling
            if (info.FieldType === 5 && info.FieldValue) {
                // Date only: use Date object
                const dateObj = new Date(info.FieldValue)
                setValue(
                    'value',
                    Number.isNaN(dateObj.getTime()) ? '' : dateObj,
                )
            } else if (info.FieldType === 4 && info.FieldValue) {
                // Datetime-local: use string in YYYY-MM-DDTHH:mm
                const dateObj = new Date(info.FieldValue)
                if (!Number.isNaN(dateObj.getTime())) {
                    const pad = n => n.toString().padStart(2, '0')
                    const formatted = `${dateObj.getFullYear()}-${pad(
                        dateObj.getMonth() + 1,
                    )}-${pad(dateObj.getDate())}T${pad(
                        dateObj.getHours(),
                    )}:${pad(dateObj.getMinutes())}`
                    setValue('value', formatted)
                } else {
                    setValue('value', '')
                }
            } else {
                setValue('value', info.FieldValue || '')
            }
            setValue('showInReport', !!info.ShowInReport)
        }
    }, [additionalInfoModalOpen, effectiveAdditionalInfoList, setValue])

    // Use filtered list for display and API
    const filteredAdditionalInfoList =
        filterNonEmptyAdditionalInfo(additionalInfoList)

    // Handler for closing the modal (add or edit)
    const handleAdditionalInfoModalClose = info => {
        setAdditionalInfoModalOpen(false)
        if (info) {
            if (editAdditionalInfoIndex !== null) {
                // Edit mode: update the entry at the index
                setAdditionalInfoList(list =>
                    list.map((item, idx) =>
                        idx === editAdditionalInfoIndex ? info : item,
                    ),
                )
            } else {
                // Add mode: append the new info
                setAdditionalInfoList(list => [...list, info])
            }
        }
        setEditAdditionalInfoIndex(null)
    }

    return (
        <Form noValidate>
            {/* Desktop: Add Business Entity Group button positioned above form */}
            <DesktopMode>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                    <AppPrimaryButton
                        type="button"
                        onClick={openAddGroupModal}
                    >
                        Add Business Entity Group
                    </AppPrimaryButton>
                </div>
            </DesktopMode>
            <Form.Group widths="equal">
                <FormDropdownField
                    control={control}
                    label="Customer Group"
                    name="CustomerGroupId"
                    placeholder="Select Group"
                    errors={errors?.CustomerGroupId}
                    options={customerGroups.map(g => ({
                        key: g.CustomerGroupId,
                        value: g.CustomerGroupId.toString(),
                        text: g.CustomerGroupName,
                    }))}
                    defaultValue={watch('CustomerGroupId') || ''}
                    search
                    required
                />
                {/* Desktop: Edit icon button */}
                <DesktopMode>
                    <Form.Field>
                        <label>&nbsp;</label>
                        <AppPrimaryButton
                            type="button"
                            onClick={openEditGroupModal}
                            disabled={!watch('CustomerGroupId')}
                            fluid
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            <PencilSimple size={15} />
                        </AppPrimaryButton>
                    </Form.Field>
                </DesktopMode>
                {/* Mobile-only buttons */}
                <MobileMode>
                    <Form.Field>
                        <label>&nbsp;</label>
                        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                            <AppPrimaryButton
                                type="button"
                                onClick={openAddGroupModal}
                                style={{ flex: 1 }}
                            >
                               Add Business Entity Group
                            </AppPrimaryButton>
                            <AppPrimaryButton
                                type="button"
                                onClick={openEditGroupModal}
                                disabled={!watch('CustomerGroupId')}
                                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <PencilSimple size={15} />
                            </AppPrimaryButton>
                        </div>
                    </Form.Field>
                </MobileMode>
            </Form.Group>
            <Form.Group widths="equal">
                <FormTextFormField
                    control={control}
                    label="Customer Name"
                    name="customer_name"
                    placeholder="Customer Name"
                    errors={errors?.customer_name}
                    defaultValue=""
                    required
                />
                <FormTextFormField
                    control={control}
                    label="Email"
                    name="Email"
                    placeholder="Email"
                    errors={errors?.Email}
                    defaultValue=""
                />
                <FormTextFormField
                    control={control}
                    label="Phone"
                    name="Phone"
                    placeholder="Phone"
                    errors={errors?.Phone}
                    defaultValue=""
                    type="number"
                />
            </Form.Group>
            <FormTextAreaFormField
                control={control}
                label="Address"
                name="Address"
                placeholder="Address"
                errors={errors?.Address}
                defaultValue=""
                rows={5}
            />
            <Form.Group widths="equal">
                <FormTextFormField
                    control={control}
                    label="City"
                    name="City"
                    placeholder="City"
                    errors={errors?.City}
                    defaultValue=""
                />
                <FormTextFormField
                    control={control}
                    label="State"
                    name="State"
                    placeholder="State"
                    errors={errors?.State}
                    defaultValue=""
                />
                <FormTextFormField
                    control={control}
                    label="Zip"
                    name="Zip"
                    placeholder="Zip"
                    errors={errors?.Zip}
                    defaultValue=""
                    type="number"
                />
            </Form.Group>
            <SubmitCancelButtons
                onSubmit={handleSubmit(submit)}
                loading={loading}
                onCancel={onCancel}
            />
            <Modal
                open={groupModalOpen}
                onClose={() => setGroupModalOpen(false)}
                size="small"
                className="model"
            >
                <Modal.Header>
                    <FlexRowBetween>
                        <TitleText>
                            {groupModalMode === 'add'
                                ? 'Create Business Entity Group'
                                : 'Edit Business Entity Group'}
                        </TitleText>
                        <X
                            className="pointer"
                            size={32}
                            onClick={() => setGroupModalOpen(false)}
                        />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content>
                    <Form>
                        <Form.Input
                            label="Group Name"
                            value={newGroupName}
                            onChange={e => setNewGroupName(e.target.value)}
                            placeholder="Group Name"
                            error={groupNameTriedSubmit && !newGroupName.trim() ? { content: 'Group Name is required' } : null}
                        />
                        <Form.Group widths="equal">
                            <Form.Input
                                label="Email"
                                value={groupEmail}
                                placeholder="Email"
                                onChange={e => setGroupEmail(e.target.value)}
                            />
                            <Form.Input
                                label="Phone"
                                value={groupPhone}
                                placeholder="Phone"
                                onChange={e => setGroupPhone(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <Form.Input
                                label="Address Line 1"
                                value={addressLine1}
                                placeholder="Address Line 1"
                                onChange={e => setAddressLine1(e.target.value)}
                            />
                            <Form.Input
                                label="Address Line 2"
                                value={addressLine2}
                                placeholder="Address Line 2"
                                onChange={e => setAddressLine2(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <Form.Input
                                label="City"
                                value={city}
                                placeholder="City"
                                onChange={e => setCity(e.target.value)}
                            />
                            <Form.Input
                                label="State/Province"
                                value={stateProvince}
                                placeholder="State/Province"
                                onChange={e => setStateProvince(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <Form.Input
                                label="Postal Code"
                                value={postalCode}
                                placeholder="Postal Code"
                                onChange={e => setPostalCode(e.target.value)}
                            />
                            <Form.Input
                                label="Country"
                                value={country}
                                placeholder="Country"
                                onChange={e => setCountry(e.target.value)}
                            />
                        </Form.Group>
                        <FileUploadPicker
                            label="Logo"
                            files={logoFiles}
                            addedNewFiles={list => setLogoFiles(list)}
                            maxFiles={1}
                            accept={{ 'image/*': ['.png', '.jpg', '.jpeg'] }}
                            showTakePicture={false}
                            uploadButtonText="Upload Picture"
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    {groupModalMode === 'edit' && (
                        <Button
                            color="red"
                            onClick={() => setIsInactiveSelected(!isInactiveSelected)}
                            style={{ marginRight: 'auto' }}
                        >
                            {isInactiveSelected ? 'Active' : 'Inactive'}
                        </Button>
                    )}
                    <Button onClick={() => {
                        setGroupModalOpen(false)
                        setIsInactiveSelected(false)
                    }}>
                        Cancel
                    </Button>
                    <Button
                        primary
                        onClick={handleGroupModalSubmit}
                        disabled={!newGroupName.trim()}
                    >
                        {groupModalMode === 'add' ? 'Create' : 'Update'}
                    </Button>
                </Modal.Actions>
            </Modal>
            <Modal
                open={additionalInfoModalOpen}
                onClose={() => {
                    setAdditionalInfoModalOpen(false)
                    setEditAdditionalInfoIndex(null)
                }}
                className="model"
                size="small"
            >
                <Modal.Header>
                    <FlexRowBetween>
                        <TitleText>
                            {editAdditionalInfoIndex !== null
                                ? 'Edit Additional Information'
                                : 'Add Additional Information'}
                        </TitleText>
                        <X
                            className="pointer"
                            size={32}
                            onClick={() => {
                                setAdditionalInfoModalOpen(false)
                                setEditAdditionalInfoIndex(null)
                            }}
                        />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content>
                    {/* Show Existing Additional Info only in edit mode and if there are entries */}
                    {editAdditionalInfoIndex !== null &&
                        additionalInfoList &&
                        additionalInfoList.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                                <b>Existing Additional Info:</b>
                                {additionalInfoList.map((info, idx) => (
                                    <div
                                        key={info.UniqueId || idx}
                                        style={{
                                            padding: '4px 0',
                                            borderBottom: '1px solid #eee',
                                        }}
                                    >
                                        <span>
                                            <b>{info.FieldName}</b> (
                                            {info.FieldType}) :{' '}
                                            {info.FieldValue}{' '}
                                            {info.ShowInReport
                                                ? '[Show in Report]'
                                                : ''}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    <CustomerAdditionalInfoModal
                        open={additionalInfoModalOpen}
                        onClose={handleAdditionalInfoModalClose}
                        customer={data}
                        additionalInfoList={
                            editAdditionalInfoIndex !== null
                                ? [
                                      filteredAdditionalInfoList[
                                          editAdditionalInfoIndex
                                      ],
                                  ]
                                : []
                        }
                        customerList={customerList}
                        mode={editAdditionalInfoIndex !== null ? 'edit' : 'add'}
                    />
                </Modal.Content>
            </Modal>
        </Form>
    )
}

// Add a dedicated modal for editing additional info
export const CustomerAdditionalInfoModal = ({
    open,
    onClose,
    customer,
    additionalInfoList,
    customerList,
    mode = 'edit',
}) => {
    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(additionalInfoSchema),
        defaultValues: {
            label: '',
            labelType: '',
            value: '',
            showInReport: false,
        },
    })
    // Pre-fill on open (only for edit mode)
    useEffect(() => {
        if (
            open &&
            mode === 'edit' &&
            additionalInfoList &&
            additionalInfoList.length > 0
        ) {
            const info = additionalInfoList[0]
            setValue('label', info.FieldName || '')
            setValue('labelType', fieldTypeToLabelType[info.FieldType] || '')
            // Robust date/datetime-local handling
            if (info.FieldType === 5 && info.FieldValue) {
                // Date only: use Date object
                const dateObj = new Date(info.FieldValue)
                setValue(
                    'value',
                    Number.isNaN(dateObj.getTime()) ? '' : dateObj,
                )
            } else if (info.FieldType === 4 && info.FieldValue) {
                // Datetime-local: use string in YYYY-MM-DDTHH:mm
                const dateObj = new Date(info.FieldValue)
                if (!Number.isNaN(dateObj.getTime())) {
                    const pad = n => n.toString().padStart(2, '0')
                    const formatted = `${dateObj.getFullYear()}-${pad(
                        dateObj.getMonth() + 1,
                    )}-${pad(dateObj.getDate())}T${pad(
                        dateObj.getHours(),
                    )}:${pad(dateObj.getMinutes())}`
                    setValue('value', formatted)
                } else {
                    setValue('value', '')
                }
            } else {
                setValue('value', info.FieldValue || '')
            }
            setValue('showInReport', !!info.ShowInReport)
        } else if (open && mode === 'add') {
            setValue('label', '')
            setValue('labelType', '')
            setValue('value', '')
            setValue('showInReport', false)
        }
    }, [open, additionalInfoList, setValue, mode])
    // On submit
    const handleSubmitAdditionalInfo = handleSubmit(async formData => {
        if (!customer && mode === 'edit') return
        let fullCustomer = customer
        if (mode === 'edit') {
            fullCustomer = customerList.find(
                c => c.unique_id === customer.unique_id,
            )
        } else if (mode === 'add') {
            fullCustomer =
                customerList.find(c => c.unique_id === customer?.unique_id) ||
                customer
        }
        let updatedAdditionalInfoList
        if (mode === 'edit') {
            updatedAdditionalInfoList = additionalInfoList.map((info, idx) =>
                idx === 0
                    ? {
                          ...info,
                          FieldName: formData.label,
                          FieldType: labelTypeToFieldType[formData.labelType],
                          FieldValue: formData.value,
                          ShowInReport: formData.showInReport,
                      }
                    : info,
            )
        } else {
            // Add mode: append new entry
            updatedAdditionalInfoList = [
                ...((fullCustomer && fullCustomer.CustomerAdditionalInfo) ||
                    []),
                {
                    UniqueId: 0,
                    CustomerId: fullCustomer?.unique_id || null,
                    FieldName: formData.label,
                    FieldType: labelTypeToFieldType[formData.labelType],
                    FieldValue: formData.value,
                    ShowInReport: formData.showInReport,
                },
            ]
        }
        const payload = {
            ...fullCustomer,
            CustomerAdditionalInfo: updatedAdditionalInfoList,
        }
        if (
            mode === 'edit' ||
            (mode === 'add' &&
                customer &&
                typeof customer.unique_id === 'number')
        ) {
            try {
                await CustomerService.update(payload)
                onClose(payload)
            } catch (e) {
                onClose(payload)
            }
        } else {
            // In add mode, just return the new info to the parent (main screen add)
            onClose(
                updatedAdditionalInfoList[updatedAdditionalInfoList.length - 1],
            )
        }
    })
    return (
        <Modal open={open} onClose={onClose} className="model" size="small">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>
                        {mode === 'add'
                            ? 'Add Additional Information'
                            : 'Edit Additional Information'}
                    </TitleText>
                    <X className="pointer" size={32} onClick={onClose} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                {/* Show Existing Additional Info only in edit mode and if there are entries */}
                {mode === 'edit' &&
                    additionalInfoList &&
                    additionalInfoList.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <b>Existing Additional Info:</b>
                            {additionalInfoList.map((info, idx) => (
                                <div
                                    key={info.UniqueId || idx}
                                    style={{
                                        padding: '4px 0',
                                        borderBottom: '1px solid #eee',
                                    }}
                                >
                                    <span>
                                        <b>{info.FieldName}</b> (
                                        {info.FieldType}) : {info.FieldValue}{' '}
                                        {info.ShowInReport
                                            ? '[Show in Report]'
                                            : ''}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                <Form>
                    <FormTextFormField
                        control={control}
                        label="Label"
                        name="label"
                        placeholder="Label"
                        errors={errors?.label}
                        defaultValue=""
                        required
                    />
                    <FormDropdownField
                        control={control}
                        label="Label Type"
                        name="labelType"
                        options={labelTypeOptions}
                        placeholder="Select Type"
                        errors={errors?.labelType}
                        onChange={(_, { value }) => {
                            setValue('labelType', value)
                            setValue('value', '')
                        }}
                        required
                    />
                    {watch('labelType') === 'numeric' && (
                        <FormTextFormField
                            control={control}
                            label="Value"
                            name="value"
                            type="number"
                            placeholder="Enter number"
                            errors={errors?.value}
                            required
                        />
                    )}
                    {watch('labelType') === 'text' && (
                        <FormTextFormField
                            control={control}
                            label="Value"
                            name="value"
                            placeholder="Enter text"
                            errors={errors?.value}
                            required
                        />
                    )}
                    {watch('labelType') === 'textarea' && (
                        <FormTextAreaFormField
                            control={control}
                            label="Value"
                            name="value"
                            placeholder="Enter text area"
                            errors={errors?.value}
                            rows={4}
                            required
                        />
                    )}
                    {watch('labelType') === 'datetime' && (
                        <Form.Input
                            label="Value"
                            name="value"
                            type="datetime-local"
                            value={watch('value')}
                            onChange={(_, { value }) =>
                                setValue('value', value)
                            }
                            placeholder="Select date/time"
                            required
                        />
                    )}
                    {watch('labelType') === 'date' && (
                        <FormDateField
                            control={control}
                            label="Value"
                            name="value"
                            type="basic"
                            position="top right"
                            errors={errors?.value}
                            required
                        />
                    )}
                    <Form.Field style={{ marginTop: 16 }}>
                        <Checkbox
                            label="Show in the report"
                            checked={!!watch('showInReport')}
                            onChange={(_, { checked }) =>
                                setValue('showInReport', checked)
                            }
                        />
                    </Form.Field>
                    <SubmitCancelButtons
                        onSubmit={handleSubmitAdditionalInfo}
                        onCancel={onClose}
                    />
                </Form>
            </Modal.Content>
        </Modal>
    )
}
